import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import Grid from '@material-ui/core/Grid'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import Recaptcha from '@components/recaptcha'
import PropTypes from 'prop-types'
import MarketingOptIn from '@components/marketing-opt-in'
import EmailFieldWithSuggestions from '@components/form-utils/email-field-with-suggestions'
import { getLeadIntentOptions } from '@components/form-utils/lead-intent-options'
import SidebarLeadForm from './coach-profile-sidebar-lead-form'
import useCoachingLeadForm from './use-coaching-lead-form'
import { useStyles } from './styles'

export const FLAGGED_CONTACT_SUBTITLE_WITH_SERVICES = 'Not sure which service to pick? Send a request to start a conversation.'
export const FLAGGED_CONTACT_SUBTITLE_WITHOUT_SERVICES = 'Send a request to start a conversation with this coach.'

function LeadForm({
  coach,
  barePageLayout,
  hasListedServices = false,
  deferRecaptchaUntilVisible = false,
  recaptchaHostVisible = true,
  alreadySubmitted = false,
  onSubmitted,
}) {
  const classes = useStyles()
  const {
    form: {
      register, control, handleSubmit, errors,
    },
    coachName,
    submitted,
    submittedHere,
    onSubmit,
    submitErrorMessage,
    shouldMountRecaptcha,
    setRecaptchaToken,
    marketingOptInChecked,
    setMarketingOptInChecked,
    submitDisabled,
  } = useCoachingLeadForm({
    coach,
    position: 'in-page',
    deferRecaptchaUntilVisible,
    recaptchaHostVisible,
    alreadySubmitted,
    onSubmitted,
  })

  const intentOptions = getLeadIntentOptions(coach.customSlug || coachName)
  const flaggedSubtitle = hasListedServices
    ? FLAGGED_CONTACT_SUBTITLE_WITH_SERVICES
    : FLAGGED_CONTACT_SUBTITLE_WITHOUT_SERVICES

  useEffect(() => {
    if (submittedHere) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [submittedHere])

  if (submitted) {
    return (
      <Container
        data-testid="lead-form"
      >
        <Grid container justifyContent="center">
          <Grid item md={6}>
            <Box pb={1}>
              <Typography style={{ textAlign: 'center' }} variant="h3">Thanks for your interest!</Typography>
            </Box>
            <Box>
              <Typography style={{ textAlign: 'center' }}>
                Your information has been shared with Coach{' '}
                {coach.firstName}. You will also receive an email
                confirmation.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    )
  }

  return (
    <Container
      data-testid="lead-form"
      id="lead-form"
      maxWidth={barePageLayout ? false : 'lg'}
      disableGutters={barePageLayout}
    >
      <Grid container justifyContent={barePageLayout ? 'flex-start' : 'center'}>
        <Grid
          item
          xs={12}
          {...(barePageLayout ? {} : { sm: 12, md: 8 })}
          data-testid="lead-form-column"
          data-lead-form-width={barePageLayout ? 'content-column' : 'legacy'}
        >
          <Box
            className={barePageLayout ? classes.flaggedFormPanel : undefined}
            data-testid={barePageLayout ? 'lead-form-panel' : undefined}
          >
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box>
                {barePageLayout ? null : (
                  <Typography component="p" className={classes.contactOverline}>
                  Request coaching
                  </Typography>
                )}
                <Typography
                  variant={barePageLayout ? 'h5' : undefined}
                  component="h2"
                  className={barePageLayout
                    ? classes.contactHeadlineFlagged
                    : classes.contactHeadline}
                >
                  {barePageLayout ? 'Ask the Coach' : 'Ready to Transform Your Game?'}
                </Typography>
                <Typography
                  component="p"
                  className={barePageLayout
                    ? `${classes.contactSubtitle} ${classes.contactSubtitleFullWidth}`
                    : classes.contactSubtitle}
                >
                  {barePageLayout
                    ? flaggedSubtitle
                    : 'Just your name, email, and what you\'re looking for — takes less than 30 seconds'}
                </Typography>
              </Box>

              <Box mt={4}>
                <Box mb={1}>
                  <Typography variant="subtitle1">Your Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <TextField
                        id="firstName"
                        name="firstName"
                        label="First name"
                        required
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        inputRef={register}
                        variant="outlined"
                        className={classes.formInput}
                        autoComplete="given-name"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <TextField
                        id="lastName"
                        name="lastName"
                        label="Last name"
                        required
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        inputRef={register}
                        variant="outlined"
                        className={classes.formInput}
                        autoComplete="family-name"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <TextField
                        id="phoneNumber"
                        name="phoneNumber"
                        label="Phone number"
                        required
                        type="tel"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                        inputRef={register}
                        variant="outlined"
                        className={classes.formInput}
                        autoComplete="tel-national"
                        inputProps={{
                          maxLength: 13,
                        }}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/[^\d- ]/g, '')
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <EmailFieldWithSuggestions
                        id="email"
                        fieldName="email"
                        control={control}
                        errors={errors}
                        label="Email"
                        variant="outlined"
                        className={classes.formInput}
                        fullWidth
                        required
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Box mt={2} mb={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" style={{ marginBottom: '10px' }}>What can {coach.firstName} help you with?</Typography>
                    <FormControl className={classes.goalSelectControl} variant="outlined" fullWidth>
                      <InputLabel id="interest-label">Select a goal *</InputLabel>
                      <Controller
                        defaultValue=""
                        name="interest"
                        control={control}
                        as={(
                          <Select
                            name="interest"
                            required
                            label="Select a goal *"
                            id="select-interest"
                            labelId="interest-label"
                            error={!!errors.interest}
                            MenuProps={{
                              elevation: 0,
                              getContentAnchorEl: null,
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                              transformOrigin: { vertical: 'top', horizontal: 'left' },
                              classes: { paper: classes.selectMenuPaper },
                            }}
                          >
                            {intentOptions.map((opt) => (
                              <MenuItem key={opt} value={opt} className={classes.selectMenuItem}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Box className={classes.leadFormOptInSpacing}>
                      <MarketingOptIn
                        checked={marketingOptInChecked}
                        onChange={setMarketingOptInChecked}
                        useMarketingOptIn="callout"
                        stackCallout={barePageLayout}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} className={classes.recaptchaGridItem}>
                    <Box className={classes.recaptchaSection}>
                      {shouldMountRecaptcha ? (
                        <Recaptcha setToken={setRecaptchaToken} {...(barePageLayout ? { size: 'normal' } : {})} />
                      ) : null}
                      {submitErrorMessage ? (
                        <FormHelperText component="div" error className={classes.recaptchaSubmitError}>
                          {submitErrorMessage}
                        </FormHelperText>
                      ) : null}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box mt={2.5}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitDisabled}
                >
                  {barePageLayout ? 'Send Request' : 'Submit'}
                </Button>
              </Box>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

function CoachProfileLeadForm({
  sidebar = false, coach, alreadySubmitted, onSubmitted, ...props
}) {
  if (sidebar) {
    return (
      <SidebarLeadForm
        coach={coach}
        alreadySubmitted={alreadySubmitted}
        onSubmitted={onSubmitted}
      />
    )
  }
  return (
    <LeadForm
      coach={coach}
      alreadySubmitted={alreadySubmitted}
      onSubmitted={onSubmitted}
      {...props}
    />
  )
}

export default CoachProfileLeadForm

CoachProfileLeadForm.propTypes = {
  /**
   * Compact two-step card for the flagged profile's md+ sidebar (coaches
   * without the Book a lesson widget). Ignores the in-page layout props.
   */
  sidebar: PropTypes.bool,
  /** In-page layout only: flagged full content-column panel. */
  barePageLayout: PropTypes.bool,
  /** In-page layout only: helper copy prefers booking a listed service first. */
  hasListedServices: PropTypes.bool,
  /** In-page layout only: do not mount reCAPTCHA until `recaptchaHostVisible`. */
  deferRecaptchaUntilVisible: PropTypes.bool,
  /** In-page layout only: true when the form's host is visible. */
  recaptchaHostVisible: PropTypes.bool,
  /** Show the confirmation because another form on the page already sent the lead. */
  alreadySubmitted: PropTypes.bool,
  /** Called once after this form's lead is accepted. */
  onSubmitted: PropTypes.func,
  coach: PropTypes.shape({
    customSlug: PropTypes.string,
    firstName: PropTypes.string,
  }),
}

CoachProfileLeadForm.defaultProps = {
  sidebar: false,
  barePageLayout: false,
  hasListedServices: false,
  deferRecaptchaUntilVisible: false,
  recaptchaHostVisible: true,
  alreadySubmitted: false,
  onSubmitted: undefined,
}

LeadForm.propTypes = {
  barePageLayout: PropTypes.bool,
  /** When true, helper copy prefers booking a listed service first. */
  hasListedServices: PropTypes.bool,
  /** When true with a hidden host (e.g. `display:none` tab), do not mount reCAPTCHA until visible. */
  deferRecaptchaUntilVisible: PropTypes.bool,
  /** Must be true when the lead form container is visible (e.g. Contact tab selected). */
  recaptchaHostVisible: PropTypes.bool,
  alreadySubmitted: PropTypes.bool,
  onSubmitted: PropTypes.func,
  coach: PropTypes.shape({
    customSlug: PropTypes.string,
    firstName: PropTypes.string,
  }),
}

LeadForm.defaultProps = {
  barePageLayout: false,
  hasListedServices: false,
  deferRecaptchaUntilVisible: false,
  recaptchaHostVisible: true,
  alreadySubmitted: false,
  onSubmitted: undefined,
}
